/** @prettier */

import {
  Container,
  Grid,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Radio,
  Stack,
  FormControlLabel,
  CircularProgress,
  Backdrop,
  Box,
} from '@mui/material';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {useEffect, useState} from 'react';
import {useLoaderData, useParams, LoaderFunctionArgs} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {utcToZonedTime, format} from 'date-fns-tz';
import {AdapterLuxon} from '@mui/x-date-pickers/AdapterLuxon';
import {DateTime} from 'luxon';

//API import
import {
  getCompositionsGateway,
  getCompositionsRTUnit,
  getCompositionsSLUnit,
  getCompositionsMoubusUnit,
  getCompositionsGroups,
  getCompositionsSitesBySiteID,
  getHistorySitesBySiteID,
  getHistorySitesByUnitID,
  postHistorySitesByUnitID,
  deleteHistorySitesByUnitID,
  getCompositionsSLIFUnit,
  getSettingUser,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//MTH component import
import {PrimaryButton} from '../../component/BasicButton';
import TextFieldSelectBox from '../../component/SelectBox';

import {apiErrorAlert} from '../../functions/apiErrorAlert';
import {pullDownMenu} from '../../define';
import {checkUnitExistence} from '../../functions/checkUnitExistence';
import type {unitExistence} from '../../functions/checkUnitExistence';
import {checkSiteInfoBillingStatus} from '../../functions/checkBillingStatus';
import {limit} from '../../define';
import {decideLuxonDatePickerLocale} from '../../functions/decideLuxonDatePickerLocale';

type UnitDataType = {
  gwName: string;
  gwID: string;
  address: string;
  unitName: string;
  unitID: string;
};

type LoaderReturnType = {
  /** タイムゾ－ン */
  timezone: apiIF.res.getCompositionsSitesBySiteID['timezone'];
  /** ゲートウェイ情報 */
  gwInfo: apiIF.res.getCompositionsGateway['gateway-informations'];
  /** グループデータ */
  groupInfo: apiIF.res.getCompositionsGroups['group-informations'];
  /** 生成済み、生成中の状態値データ */
  historyData: apiIF.res.getHistorySitesBySiteID['history-data'];
};
export async function loader({
  params,
}: LoaderFunctionArgs): Promise<LoaderReturnType | undefined> {
  try {
    if (params.siteID === undefined) return;

    const promises = [];
    // サイト情報
    promises.push(getCompositionsSitesBySiteID(params.siteID));
    // ゲートウェイ情報
    promises.push(
      getCompositionsGateway(params.siteID, {
        'fields': 'gateway-name,compositions,billing-status',
        'max-result': limit.REGISTRATION_NUM_GW_MAX,
      })
    );
    //グループデータ
    promises.push(
      getCompositionsGroups(params.siteID, {
        'fields': 'group-name,unit-system,unit-informations',
        'max-result': limit.REGISTRATION_NUM_GROUP_MAX,
      })
    );
    // 生成済み、生成中の状態値データ
    promises.push(getHistorySitesBySiteID(params.siteID));
    //ユニットの登録状況を取得
    promises.push(checkUnitExistence(params.siteID, true));

    const response = await Promise.all(promises);
    const site = response[0] as apiIF.res.getCompositionsSitesBySiteID;
    const gwInfo = response[1] as apiIF.res.getCompositionsGateway;
    const groupInfo = response[2] as apiIF.res.getCompositionsGroups;
    const historyData = response[3] as apiIF.res.getHistorySitesBySiteID;
    const unitExistence = response[4] as unitExistence;

    //全てのユニットが未登録ならリダイレクト
    const unregistered = Object.values(unitExistence).every(value => {
      return value === false;
    });
    if (unregistered) {
      throw new Error(
        'Required equipment not registered or insufficient permission'
      );
    }

    //全GWに課金情報がなければリダイレクト
    if (checkSiteInfoBillingStatus(site['billing-status']) === false) {
      throw new Error('Billing status does not exist');
    }

    return {
      timezone: site.timezone,
      gwInfo: gwInfo['gateway-informations'],
      groupInfo: groupInfo['group-informations'],
      historyData: historyData['history-data'],
    };
  } catch {
    return undefined;
  }
}

/**
 * ヒストリー画面
 * @returns JSX.Element
 */
const OperationDataHistory = () => {
  const trans = useTranslation().t; //翻訳関数
  const loaderData = useLoaderData() as LoaderReturnType | undefined;
  const {siteID} = useParams(); // サイトID
  /**
   * タイムゾーンが存在する場合、そのタイムゾーンに基づいた現在の日付と時刻
   * 存在しない場合、ブラウザ依存の現在時刻
   */
  const nowDate = loaderData
    ? DateTime.now().setZone(loaderData.timezone).startOf('day')
    : DateTime.now().startOf('day');

  //prettier-ignore
  const [timeZone, setTimeZone] = useState<apiIF.res.getCompositionsSitesBySiteID['timezone']>(''); // タイムゾーン
  //prettier-ignore
  const [unitSetting, setUnitSetting] = useState<'--' | 'airConditioner' | 'qton' | 'hccvq' | 'chiller'>('--'); // カテゴリー選択
  //prettier-ignore
  const [filterSetting, setFilterSetting] = useState<'' | '--' | 'indoorUnit' | 'outdoorUnit'>(''); // フィルター選択
  const [groupSetting, setGroupSetting] = useState<string>(''); // グループ選択
  const [tableData, setTableData] = useState<UnitDataType[]>([]); // ユニット選択テーブル
  const [selectedNumber, setSelectedNumber] = useState<number>(0); // ユニット選択番号
  const [beforeTimeRequired, setBeforeTimeRequired] = useState(nowDate); // データ取得終了日時
  //prettier-ignore
  const [afterTimeRequired, setAfterTimeRequired] = useState(nowDate.minus({month:1})); // データ取得開始日時
  const [indoorUnits, setIndoorUnits] = useState<UnitDataType[]>([]); // SLインドアユニット、HCCV(Q)インドアユニットの情報保管用(グループ情報とユニット情報を別で取っているため)
  //prettier-ignore
  const [historyData, setHistoryData] = useState<apiIF.res.getHistorySitesBySiteID['history-data'] | null>(null); // 生成済み、生成中の状態値ファイルの一覧
  const [apiWait, setAPIWait] = useState<boolean>(false); // API応答待ちフラグ
  //prettier-ignore
  const [groupInfo, setGroupInfo] =useState<apiIF.res.getCompositionsGroups['group-informations'] | null>(null); // グループ情報
  //prettier-ignore
  const [gwInfo, setGwInfo] = useState<apiIF.res.getCompositionsGateway['gateway-informations'] | null>(null); // ゲートウェイ情報
  //prettier-ignore
  const [language, setLanguage] = useState<apiIF.res.getSettingUser['language']>('english'); // 言語情報

  useEffect(() => {
    if (loaderData === undefined) return;

    setGroupInfo(loaderData.groupInfo);
    setTimeZone(loaderData.timezone);
    setHistoryData(loaderData.historyData);
    setGwInfo(loaderData.gwInfo);
  }, [loaderData]);

  useEffect(() => {
    if (unitSetting === 'airConditioner') {
      //空調機の場合、室内機固定となる
      setFilterSetting('indoorUnit');
      choiceSLIndoorUnit();
    } else if (unitSetting === 'hccvq') {
      setFilterSetting('--');
    } else {
      setFilterSetting('');
    }
  }, [unitSetting]);

  useEffect(() => {
    setSelectedNumber(0); // 選択しているユニットを一番上のものにする
    if (filterSetting === 'indoorUnit') {
      setGroupSetting('--');
    } else {
      setGroupSetting('');
    }
  }, [unitSetting, filterSetting]);

  //言語切り替え時、言語情報更新
  useEffect(() => {
    getSettingUser().then(response => {
      setLanguage(response.language);
    });
  }, [trans]);

  /**
   * グループ選択時の処理
   */
  useEffect(() => {
    if (groupInfo === null || groupSetting === '' || groupSetting === '--') {
      return setTableData([]);
    }

    const tempTableData: UnitDataType[] = [];
    const tempGroupUnit = groupInfo[Number(groupSetting)]['unit-informations'];
    // 選択されたグループにユニット情報を入れる
    if (tempGroupUnit === undefined) return;
    tempGroupUnit.forEach(groupUnit => {
      indoorUnits.forEach(indoorUnit => {
        if (indoorUnit['unitID'] === groupUnit['unit-id']) {
          tempTableData.push(indoorUnit);
        }
      });
    });
    setTableData(tempTableData);
  }, [groupSetting, groupInfo, indoorUnits, unitSetting]);

  // 20秒毎に状態値ファイルリストを更新する
  const [now, setNow] = useState<Date>(new Date());
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (siteID === undefined) return;

    const intervalId = setInterval(() => {
      setNow(new Date()); //useEffectが実行される度に、stateを更新し、次のuseEffectを実行する
      try {
        getHistorySitesBySiteID(siteID).then(hist => {
          setHistoryData(hist['history-data']);
        });
      } catch (error) {
        apiErrorAlert(trans);
      }
    }, 20000);

    //20秒毎にタイマを削除
    return function () {
      clearInterval(intervalId);
    };
  }, [now, siteID, trans]);

  /**
   * グループ設定用セレクトボックスの項目生成して返す
   * @returns グループ設定用セレクトボックスの項目
   */
  const setGroupChoices = () => {
    const result: {value: string; label: string}[] = [
      {value: '--', label: '---'},
    ];
    if (groupInfo === null) return result;
    for (const [index, group] of groupInfo.entries()) {
      if (group['group-name'] === undefined) {
        continue;
      }
      if (
        (unitSetting === 'airConditioner' && group['unit-system'] === 'SL') ||
        (unitSetting === 'hccvq' &&
          (group['unit-system'] === 'RT' || group['unit-system'] === 'Modbus'))
      ) {
        result.push({
          value: index.toString(),
          label: group['group-name'],
        });
      }
    }
    return result;
  };

  /**
   * 状態値ファイルの生成をリクエストする
   */
  const createHistoryFile = async () => {
    if (siteID === undefined) return;
    setAPIWait(true);
    // 開始時間と終了時間をUTCに整形する
    const afterTime = afterTimeRequired.startOf('day').toUTC().toISO();
    const beforeTime = beforeTimeRequired
      // 選択日の現地時間23:59に変換する
      .startOf('day') //X日 00:00
      .plus({day: 1}) //X+1日 00:00
      .minus({minute: 1}) //X日 23:59
      .toUTC()
      .toISO();

    // 開始日・終了日がエラーの場合
    if (afterTime === null || beforeTime === null) {
      setAPIWait(false);
      return;
    }

    try {
      await postHistorySitesByUnitID(
        siteID,
        tableData[selectedNumber].gwID,
        tableData[selectedNumber].unitID,
        {
          'before-time': beforeTime,
          'after-time': afterTime,
        }
      );
      const hist = await getHistorySitesBySiteID(siteID);
      setHistoryData(hist['history-data']);
    } catch (err) {
      apiErrorAlert(trans);
    } finally {
      setAPIWait(false);
    }
  };

  /**
   * 指定した状態値ファイルのダウンロード
   * @param gwID 指定したユニットの属するゲートウェイのID
   * @param unitID 指定したユニットのID
   * @param after データ取得開始時間
   * @param before データ取得終了時間
   */
  const downloadHistoryFile = async (
    gwID: string,
    unitID: string,
    after: string,
    before: string
  ) => {
    if (siteID === undefined) return;
    setAPIWait(true);
    try {
      const path = await getHistorySitesByUnitID(siteID, gwID, unitID, {
        'before-time': before,
        'after-time': after,
      });
      const link = document.createElement('a');
      link.href = path['download-path'];
      link.click();
    } catch (err) {
      apiErrorAlert(trans);
    } finally {
      setAPIWait(false);
    }
  };

  /**
   * 指定した状態値ファイルの生成の中止
   * @param gwID 指定したユニットの属するゲートウェイのID
   * @param unitID 指定したユニットのID
   * @param after データ取得開始時間
   * @param before データ取得終了時間
   */
  const deleteHistoryFile = async (
    gwID: string,
    unitID: string,
    after: string,
    before: string
  ) => {
    if (siteID === undefined) return;
    setAPIWait(true);
    try {
      await deleteHistorySitesByUnitID(siteID, gwID, unitID, {
        'before-time': before,
        'after-time': after,
      });
      const hist = await getHistorySitesBySiteID(siteID);
      setHistoryData(hist['history-data']);
    } catch (err) {
      apiErrorAlert(trans);
    } finally {
      setAPIWait(false);
    }
  };

  /**
   * UTC時間をdd/MM/yyyyにして返す
   * @param date UTC時間
   * @returns dd/MM/yyyy
   */
  const transDate = (utcDate: string) => {
    const date = format(utcToZonedTime(utcDate, timeZone), 'dd/MM/yyyy');
    const [day, month, year] = date.split('/');
    return trans('OperationDataHistory.fileList.date', {
      day: day,
      month: month,
      year: year,
    });
  };

  /**
   * AirConditioner IndoorUnit 選択時
   */
  const choiceSLIndoorUnit = async () => {
    setIndoorUnits([]);
    setTableData([]);
    if (siteID === undefined || gwInfo === null) return;
    setAPIWait(true);
    try {
      let gwInformations = []; // ユニット情報があったGWを含む配列
      let promises = [];
      for (const gw of gwInfo) {
        if (
          gw['gateway-name'] === undefined ||
          gw.compositions === undefined ||
          (gw.compositions['sl-system-1']['sl-unit'].length === 0 &&
            gw.compositions['sl-system-2']['sl-unit'].length === 0) ||
          gw['billing-status'] === 'unpaid'
        ) {
          continue;
        }
        // ユニット情報があったGWのみを保存する
        gwInformations.push(gw);
        // SL IndoorUnitのデータを集める
        promises.push(
          getCompositionsSLUnit(siteID, gw['gateway-id'], {
            'fields': 'indoor-unit-informations',
            'max-result': 400,
          })
        );
        //インターフェースの情報を集める
        promises.push(
          getCompositionsSLIFUnit(siteID, gw['gateway-id'], {
            'max-result': 400,
          })
        );
      }
      const response = await Promise.all(promises);
      let tempUnitData: UnitDataType[] = [];
      for (const [index, gw] of gwInformations.entries()) {
        const slUnitInfo = response[
          index * 2
        ] as apiIF.res.getCompositionsSLUnit;
        const slIFUnitInfo = response[
          index * 2 + 1
        ] as apiIF.res.getCompositionsSLIFUnit;

        if (gw['gateway-name'] === undefined) {
          continue;
        }
        for (const ou of slUnitInfo['outdoor-unit-informations']) {
          if (ou['indoor-unit-informations'] === undefined) {
            continue;
          }
          for (const iu of ou['indoor-unit-informations']) {
            tempUnitData.push({
              gwName: gw['gateway-name'],
              gwID: gw['gateway-id'],
              address: iu['sl-address'],
              unitName: iu['indoor-unit-name'],
              unitID: iu['unit-id'],
            });
          }
        }
        for (const iuIf of slIFUnitInfo['unit-informations']) {
          tempUnitData.push({
            gwName: gw['gateway-name'],
            gwID: gw['gateway-id'],
            address: iuIf['sl-address']!,
            unitName: iuIf['unit-name']!,
            unitID: iuIf['unit-id'],
          });
        }
      }
      setIndoorUnits(tempUnitData);
    } catch (error) {
      apiErrorAlert(trans);
    } finally {
      setAPIWait(false);
    }
  };

  /**
   * Qton選択時
   */
  const choiceQtonUnit = async () => {
    setTableData([]);
    if (siteID === undefined || gwInfo === null) return;
    setAPIWait(true);
    try {
      let gwInformations = []; // ユニット情報があったGWを含む配列
      let promises = [];
      for (const gw of gwInfo) {
        if (
          gw['gateway-name'] === undefined ||
          gw.compositions === undefined ||
          (gw.compositions['rt-system-1'].length === 0 &&
            gw.compositions['rt-system-2'].length === 0) ||
          gw['billing-status'] === 'unpaid'
        ) {
          continue;
        }
        // ユニット情報があったGWのみを保存する
        gwInformations.push(gw);
        // RTユニットのデータを集める
        promises.push(
          getCompositionsRTUnit(siteID, gw['gateway-id'], {
            'fields': 'unit-name,address,unit-type',
            'max-result': 400,
          })
        );
      }
      const response = await Promise.all(promises);
      let tempUnitData: UnitDataType[] = [];
      for (const [index, gw] of gwInformations.entries()) {
        if (gw['gateway-name'] === undefined) {
          continue;
        }
        for (const ou of response[index]['unit-informations']) {
          if (
            ou['unit-type'] === 'Hyozan' ||
            ou['unit-type'] === 'Chiller' ||
            ou['address'] === undefined ||
            ou['unit-name'] === undefined
          ) {
            continue;
          }
          tempUnitData.push({
            gwName: gw['gateway-name'],
            gwID: gw['gateway-id'],
            address: ou['address'],
            unitName: ou['unit-name'],
            unitID: ou['unit-id'],
          });
        }
      }
      setTableData(tempUnitData);
    } catch (error) {
      apiErrorAlert(trans);
    } finally {
      setAPIWait(false);
    }
  };

  /**
   * hccvq IndoorUnit 選択時
   */
  const choiceHccvqIndoorUnit = async () => {
    setTableData([]);
    if (siteID === undefined || gwInfo === null) return;
    setAPIWait(true);
    try {
      let gwInformations = []; // ユニット情報があったGWを含む配列
      let promises = [];
      for (const gw of gwInfo) {
        if (
          gw['gateway-name'] === undefined ||
          gw.compositions === undefined ||
          (gw.compositions['rt-system-1'].length === 0 &&
            gw.compositions['rt-system-2'].length === 0 &&
            gw.compositions['modbus-system'].length === 0) ||
          gw['billing-status'] === 'unpaid'
        ) {
          continue;
        }
        // ユニット情報があったGWのみを保存する
        gwInformations.push(gw);
        // RTユニットのデータを集める
        promises.push(
          getCompositionsRTUnit(siteID, gw['gateway-id'], {
            'fields': 'unit-type,indoor-unit-informations',
            'max-result': 400,
          })
        );
        // hccvqユニットのデータを集める
        promises.push(
          getCompositionsMoubusUnit(siteID, gw['gateway-id'], {
            'fields': 'indoor-unit-informations',
            'max-result': 400,
          })
        );
      }
      const response = await Promise.all(promises);
      let tempUnitData: UnitDataType[] = [];
      for (const [index, gw] of gwInformations.entries()) {
        if (gw['gateway-name'] === undefined || gw.compositions === undefined) {
          continue;
        }
        if (
          gw.compositions['rt-system-1'].length > 0 ||
          gw.compositions['rt-system-2'].length > 0
        ) {
          for (const ou of (
            response[index * 2] as apiIF.res.getCompositionsRTUnit
          )['unit-informations']) {
            if (
              ou['unit-type'] !== 'Hyozan' ||
              ou['indoor-unit-informations'] === undefined
            ) {
              continue;
            }
            for (let iu of ou['indoor-unit-informations']) {
              tempUnitData.push({
                gwName: gw['gateway-name'],
                gwID: gw['gateway-id'],
                address: iu['address'],
                unitName: iu['indoor-unit-name'],
                unitID: iu['unit-id'],
              });
            }
          }
        }
        if (gw.compositions['modbus-system'].length > 0) {
          for (const ou of (
            response[index * 2 + 1] as apiIF.res.getCompositionsMoubusUnit
          )['outdoor-unit-informations']) {
            if (ou['indoor-unit-informations'] === undefined) {
              continue;
            }
            for (let iu of ou['indoor-unit-informations']) {
              tempUnitData.push({
                gwName: gw['gateway-name'],
                gwID: gw['gateway-id'],
                address: iu['address'],
                unitName: iu['indoor-unit-name'],
                unitID: iu['unit-id'],
              });
            }
          }
        }
      }
      setIndoorUnits(tempUnitData);
    } catch (error) {
      apiErrorAlert(trans);
    } finally {
      setAPIWait(false);
    }
  };

  /**
   * HCCV(Q) OutdoorUnit　選択時
   */
  const choiceHccvqOutdoorUnit = async () => {
    setTableData([]);
    if (siteID === undefined || gwInfo === null) return;
    setAPIWait(true);
    try {
      let gwInformations = []; // ユニット情報があったGWを含む配列
      let promises = [];
      for (const gw of gwInfo) {
        if (
          gw['gateway-name'] === undefined ||
          gw.compositions === undefined ||
          (gw.compositions['rt-system-1'].length === 0 &&
            gw.compositions['rt-system-2'].length === 0 &&
            gw.compositions['modbus-system'].length === 0) ||
          gw['billing-status'] === 'unpaid'
        ) {
          continue;
        }
        // ユニット情報があったGWのみを保存する
        gwInformations.push(gw);
        // RTユニットのデータを集める
        promises.push(
          getCompositionsRTUnit(siteID, gw['gateway-id'], {
            'fields': 'unit-name,unit-type,address',
            'max-result': 400,
          })
        );
        // hccvqユニットのデータを集める
        promises.push(
          getCompositionsMoubusUnit(siteID, gw['gateway-id'], {
            'fields': 'outdoor-unit-name,ip-address',
            'max-result': 400,
          })
        );
      }
      const response = await Promise.all(promises);
      let tempUnitData: UnitDataType[] = [];
      for (const [index, gw] of gwInformations.entries()) {
        if (gw['gateway-name'] === undefined || gw.compositions === undefined) {
          continue;
        }
        if (
          gw.compositions['rt-system-1'].length > 0 ||
          gw.compositions['rt-system-2'].length > 0
        ) {
          for (const ou of (
            response[index * 2] as apiIF.res.getCompositionsRTUnit
          )['unit-informations']) {
            if (
              ou['unit-type'] !== 'Hyozan' ||
              ou['address'] === undefined ||
              ou['unit-name'] === undefined
            ) {
              continue;
            }
            tempUnitData.push({
              gwName: gw['gateway-name'],
              gwID: gw['gateway-id'],
              address: ou['address'],
              unitName: ou['unit-name'],
              unitID: ou['unit-id'],
            });
          }
        }
        if (gw.compositions['modbus-system'].length > 0) {
          for (const ou of (
            response[index * 2 + 1] as apiIF.res.getCompositionsMoubusUnit
          )['outdoor-unit-informations']) {
            if (
              ou['ip-address'] === undefined ||
              ou['outdoor-unit-name'] === undefined
            ) {
              continue;
            }
            const [, , , address] = ou['ip-address'].split('.');
            tempUnitData.push({
              gwName: gw['gateway-name'],
              gwID: gw['gateway-id'],
              address: address,
              unitName: ou['outdoor-unit-name'],
              unitID: ou['outdoor-id'],
            });
          }
        }
      }
      setTableData(tempUnitData);
    } catch (error) {
      apiErrorAlert(trans);
    } finally {
      setAPIWait(false);
    }
  };

  /**
   * Chiller選択時
   */
  const choiceChiller = async () => {
    setTableData([]);
    if (siteID === undefined || gwInfo === null) return;
    setAPIWait(true);
    try {
      let gwInformations = []; // ユニット情報があったGWを含む配列
      let promises = [];
      for (const gw of gwInfo) {
        if (
          gw['gateway-name'] === undefined ||
          gw.compositions === undefined ||
          (gw.compositions['rt-system-1'].length === 0 &&
            gw.compositions['rt-system-2'].length === 0) ||
          gw['billing-status'] === 'unpaid'
        ) {
          continue;
        }
        // ユニット情報があったGWのみを保存する
        gwInformations.push(gw);
        // RTユニットのデータを集める
        promises.push(
          getCompositionsRTUnit(siteID, gw['gateway-id'], {
            'fields': 'unit-name,address,unit-type',
            'max-result': 400,
          })
        );
      }
      const response = await Promise.all(promises);
      let tempUnitData: UnitDataType[] = [];
      for (const [index, gw] of gwInformations.entries()) {
        if (gw['gateway-name'] === undefined) {
          continue;
        }
        for (const ou of response[index]['unit-informations']) {
          if (
            ou['unit-type'] !== 'Chiller' ||
            ou['address'] === undefined ||
            ou['unit-name'] === undefined
          ) {
            continue;
          }
          tempUnitData.push({
            gwName: gw['gateway-name'],
            gwID: gw['gateway-id'],
            address: ou['address'],
            unitName: ou['unit-name'],
            unitID: ou['unit-id'],
          });
        }
      }
      setTableData(tempUnitData);
    } catch (error) {
      apiErrorAlert(trans);
    } finally {
      setAPIWait(false);
    }
  };

  /**
   * ユニット種別をチェックして表示する文言を決定する
   * @param {string} value 機種種別 unit-category
   * @returns {string} trans(翻訳文)
   */
  const unitCategoryCheck = (value: string) => {
    switch (value) {
      case 'Air Conditioner':
        return trans('OperationDataHistory.unitCategory.ac');
      case 'Q-ton':
        return trans('OperationDataHistory.unitCategory.q-ton');
      case 'HCCV(Q)':
        return trans('OperationDataHistory.unitCategory.hyozan');
      case 'Chiller':
        return trans('OperationDataHistory.unitCategory.hydrolution');
      default:
        return trans('OperationDataHistory.unitCategory.ac');
    }
  };

  return (
    <Container fixed>
      <Stack spacing={5}>
        {/** タイトル、説明 */}
        <Box>
          <h1>{trans('OperationDataHistory.title')}</h1>
          <h2>{trans('OperationDataHistory.explanation')}</h2>
        </Box>
        {/* 期間設定 */}
        <Box className="serviceInfo__operationConditionHistory__period">
          <Grid container alignItems="center" spacing={3}>
            <Grid item xs="auto">
              {trans('OperationDataHistory.period.title')}&nbsp;:&nbsp;
            </Grid>
            <Grid item xs="auto">
              <LocalizationProvider
                dateAdapter={AdapterLuxon}
                adapterLocale={decideLuxonDatePickerLocale(language)}>
                <DatePicker
                  autoFocus
                  format="dd/MM/yyyy"
                  label={trans('OperationDataHistory.startDate')}
                  maxDate={nowDate}
                  minDate={nowDate.minus({year: 10})}
                  onChange={e => {
                    if (e === null) return;
                    setAfterTimeRequired(e);
                  }}
                  slotProps={{
                    textField: {
                      error:
                        afterTimeRequired.plus({year: 1}) <
                          beforeTimeRequired ||
                        beforeTimeRequired < afterTimeRequired ||
                        afterTimeRequired.toString() === 'Invalid DateTime',
                      helperText:
                        afterTimeRequired.plus({year: 1}) <
                          beforeTimeRequired ||
                        beforeTimeRequired < afterTimeRequired ||
                        beforeTimeRequired.toString() === 'Invalid DateTime' ||
                        afterTimeRequired.toString() === 'Invalid DateTime'
                          ? trans('OperationDataHistory.period.errorText')
                          : ' ',
                    },
                  }}
                  value={afterTimeRequired}
                  views={['year', 'month', 'day']}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs="auto">
              {trans('OperationDataHistory.to')}
            </Grid>
            <Grid item xs>
              <LocalizationProvider
                dateAdapter={AdapterLuxon}
                adapterLocale={decideLuxonDatePickerLocale(language)}>
                <DatePicker
                  format="dd/MM/yyyy"
                  label={trans('OperationDataHistory.endDate')}
                  maxDate={nowDate}
                  minDate={nowDate.minus({year: 10})}
                  onChange={e => {
                    if (e === null) return;
                    setBeforeTimeRequired(e);
                  }}
                  slotProps={{
                    textField: {
                      error:
                        afterTimeRequired.plus({year: 1}) <
                          beforeTimeRequired ||
                        beforeTimeRequired < afterTimeRequired ||
                        beforeTimeRequired.toString() === 'Invalid DateTime',
                      helperText: ' ',
                    },
                  }}
                  value={beforeTimeRequired}
                  views={['year', 'month', 'day']}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </Box>
        {/* 対象 */}
        <Box className="serviceInfo__operationConditionHistory__target">
          {trans('OperationDataHistory.targetUnit.title')}
          {/* ユニット選択 */}
          <Grid
            container
            spacing={1}
            alignItems="center"
            className="serviceInfo__operationConditionHistory__target__select">
            {/** 空調機の種類 */}
            <Grid item xs={1}>
              {trans('OperationDataHistory.targetUnit.unit')}
            </Grid>
            <Grid item xs={11}>
              <TextFieldSelectBox
                choices={pullDownMenu.HISTORY_CATEGORY}
                value={unitSetting}
                setValue={e => {
                  setUnitSetting(e);
                  if (e === 'qton') {
                    choiceQtonUnit();
                  } else if (e === 'chiller') {
                    choiceChiller();
                  } else {
                    setTableData([]);
                  }
                }}
                margin="none"
                class="serviceInfo__operationConditionHistory__target__select__selectBox"
              />
            </Grid>
            {/** フィルター */}
            <Grid item xs={1}>
              {trans('OperationDataHistory.targetUnit.filter')}
            </Grid>
            <Grid item xs={3}>
              <TextFieldSelectBox
                choices={pullDownMenu.HISTORY_FILTER}
                value={filterSetting}
                setValue={e => {
                  setFilterSetting(e);
                  if (unitSetting === 'hccvq' && e === 'indoorUnit') {
                    choiceHccvqIndoorUnit();
                  } else if (unitSetting === 'hccvq' && e === 'outdoorUnit') {
                    choiceHccvqOutdoorUnit();
                  }
                }}
                margin="none"
                class="serviceInfo__operationConditionHistory__target__select__selectBox"
                disabled={unitSetting !== 'hccvq'}
              />
            </Grid>
            <Grid item xs={1}>
              {trans('OperationDataHistory.targetUnit.group')}
            </Grid>
            <Grid item xs={3}>
              <TextFieldSelectBox
                choices={setGroupChoices()}
                value={groupSetting}
                setValue={e => {
                  setGroupSetting(e);
                }}
                margin="none"
                disabled={
                  filterSetting !== 'indoorUnit' ||
                  (unitSetting !== 'airConditioner' && unitSetting !== 'hccvq')
                }
                class="serviceInfo__operationConditionHistory__target__select__selectBox"
              />
            </Grid>
          </Grid>
          {/* ユニット選択テーブル */}
          <TableContainer className="serviceInfo__operationConditionHistory__target__table">
            <Table stickyHeader size="medium" padding="none">
              <TableHead>
                <TableRow>
                  <TableCell className="serviceInfo__operationConditionHistory__target__table__radio" />
                  <TableCell className="serviceInfo__operationConditionHistory__target__table__gwName">
                    {trans('OperationDataHistory.targetUnit.gatewayName')}
                  </TableCell>
                  <TableCell className="serviceInfo__operationConditionHistory__target__table__address">
                    {trans('OperationDataHistory.targetUnit.address')}
                  </TableCell>
                  <TableCell className="serviceInfo__operationConditionHistory__target__table__unitName">
                    {trans('OperationDataHistory.targetUnit.unitName')}
                  </TableCell>
                </TableRow>
              </TableHead>
              {tableData.length > 0 && (
                <TableBody>
                  {tableData.map((value, index) => {
                    return (
                      <TableRow key={value.unitID}>
                        <TableCell className="serviceInfo__operationConditionHistory__target__table__radio">
                          <FormControlLabel
                            value={index}
                            control={<Radio color="primary" />}
                            label=""
                            onChange={() => setSelectedNumber(index)}
                            checked={index === selectedNumber}
                          />
                        </TableCell>
                        <TableCell className="serviceInfo__operationConditionHistory__target__table__gwName">
                          {value.gwName}
                        </TableCell>
                        <TableCell className="serviceInfo__operationConditionHistory__target__table__address">
                          {value.address}
                        </TableCell>
                        <TableCell className="serviceInfo__operationConditionHistory__target__table__unitName">
                          {value.unitName}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              )}
            </Table>
            {/**データがない場合に表示する */}
            {tableData.length === 0 && (
              <Box className="serviceInfo__operationConditionHistory__target__table__noDataText">
                {trans('OperationDataHistory.targetUnit.noDataText')}
              </Box>
            )}
          </TableContainer>
        </Box>
        {/** リクエストボタン */}
        <Stack direction={'column'}>
          <Box className="serviceInfo__operationConditionHistory__request">
            <PrimaryButton
              onClick={() => createHistoryFile()}
              buttonText={trans('OperationDataHistory.action.request')}
              disabled={
                tableData.length === 0 ||
                (historyData &&
                  historyData.length >= limit.OPERATION_HISTORY_REQUEST_MAX) ||
                apiWait ||
                afterTimeRequired.plus({year: 1}) < beforeTimeRequired ||
                beforeTimeRequired < afterTimeRequired ||
                beforeTimeRequired < nowDate.minus({year: 10}) ||
                afterTimeRequired < nowDate.minus({year: 10}) ||
                beforeTimeRequired.toString() === 'Invalid DateTime' ||
                afterTimeRequired.toString() === 'Invalid DateTime'
              }
              className="serviceInfo__operationConditionHistory__request__button"
            />
          </Box>
          {/* 作成数が上限に達した場合のメッセージ */}
          {historyData &&
          historyData.length >= limit.OPERATION_HISTORY_REQUEST_MAX ? (
            <Box className="serviceInfo__operationConditionHistory__helperText">
              {trans('OperationDataHistory.action.limit')}
            </Box>
          ) : (
            <></>
          )}
        </Stack>
        {/** ダウンロード状況一覧 */}
        {historyData !== null && historyData.length > 0 && (
          <>
            <h2>{trans('OperationDataHistory.fileList.title')}</h2>
            <TableContainer className="serviceInfo__operationConditionHistory__result--request">
              <Table stickyHeader size="small" padding="normal">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {trans('OperationDataHistory.fileList.unitCategory')}
                    </TableCell>
                    <TableCell>
                      {trans('OperationDataHistory.fileList.gatewayName')}
                    </TableCell>
                    <TableCell>
                      {trans('OperationDataHistory.fileList.unitName')}
                    </TableCell>
                    <TableCell>
                      {trans('OperationDataHistory.fileList.period')}
                    </TableCell>
                    <TableCell>
                      {trans('OperationDataHistory.fileList.status')}
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyData.map(value => {
                    return (
                      <TableRow
                        key={
                          value['unit-id'] +
                          value['after-date'] +
                          value['before-date']
                        }>
                        <TableCell className="serviceInfo__operationConditionHistory__result--request__category">
                          {unitCategoryCheck(value['unit-category'])}
                        </TableCell>
                        <TableCell className="serviceInfo__operationConditionHistory__result--request__gwName">
                          {value['gateway-name']}
                        </TableCell>
                        <TableCell className="serviceInfo__operationConditionHistory__result--request__unitName">
                          {value['unit-name']}
                        </TableCell>
                        <TableCell className="serviceInfo__operationConditionHistory__result--request__period">
                          {transDate(value['after-date'])}~
                          {transDate(value['before-date'])}
                        </TableCell>
                        <TableCell className="serviceInfo__operationConditionHistory__result--request__status">
                          {value['create-status'] === 'completed'
                            ? trans('OperationDataHistory.fileList.completed')
                            : trans(
                                'OperationDataHistory.fileList.processing'
                              ) +
                              trans(
                                'OperationDataHistory.fileList.remainingTime',
                                {
                                  minutes: value['time-remaining'],
                                }
                              )}
                        </TableCell>
                        <TableCell className="serviceInfo__operationConditionHistory__result--request__actions">
                          <PrimaryButton
                            onClick={() => {
                              value['create-status'] === 'completed'
                                ? downloadHistoryFile(
                                    value['gateway-id'],
                                    value['unit-id'],
                                    value['after-date'],
                                    value['before-date']
                                  )
                                : deleteHistoryFile(
                                    value['gateway-id'],
                                    value['unit-id'],
                                    value['after-date'],
                                    value['before-date']
                                  );
                            }}
                            buttonText={
                              value['create-status'] === 'completed'
                                ? trans('OperationDataHistory.action.download')
                                : trans('OperationDataHistory.action.cancel')
                            }
                            className="serviceInfo__operationConditionHistory__result--request__actions__button"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Stack>
      {/* apiの読み込み中に表示する */}
      <Backdrop
        sx={{color: '#fff', zIndex: theme => theme.zIndex.drawer + 1}}
        open={apiWait}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Container>
  );
};
export default OperationDataHistory;
